<script setup lang="ts">
import { InvalidAddressError, parseCidr } from '@it-tools/core-net'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ modelValue: string }>()
const emit = defineEmits<{ 'update:modelValue': [string]; apply: [string] }>()

const { t } = useI18n()
const draft = ref(props.modelValue)
const error = ref<string | null>(null)

watch(
  () => props.modelValue,
  (value) => {
    draft.value = value
    error.value = null
  },
)

function submit(): void {
  try {
    parseCidr(draft.value)
    error.value = null
    emit('update:modelValue', draft.value)
    emit('apply', draft.value)
  } catch (cause) {
    // Show the user a translated message; the library's message is for developers.
    error.value = cause instanceof InvalidAddressError ? t('input.invalid') : t('common.error')
  }
}
</script>

<template>
  <form class="network-input" @submit.prevent="submit">
    <label class="network-input__label" for="network">{{ t('input.network') }}</label>
    <div class="network-input__row">
      <input
        id="network"
        v-model="draft"
        class="mono"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        spellcheck="false"
        placeholder="10.0.0.0/16"
        :aria-invalid="error !== null"
        :aria-describedby="error ? 'network-error' : undefined"
      />
      <button type="submit">{{ t('input.apply') }}</button>
    </div>
    <p v-if="error" id="network-error" class="network-input__error" role="alert">{{ error }}</p>
  </form>
</template>

<style scoped>
.network-input__label {
  display: block;
  margin-bottom: var(--space-1);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.network-input__row {
  display: flex;
  gap: var(--space-2);
}

input {
  min-width: 12rem;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--text-base);
}

input[aria-invalid='true'] {
  border-color: var(--color-danger);
}

button {
  padding: var(--space-2) var(--space-4);
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: var(--color-accent);
  color: var(--color-text-inverted);
  font: inherit;
  cursor: pointer;
}

button:hover {
  background: var(--color-accent-hover);
}

.network-input__error {
  margin: var(--space-1) 0 0;
  font-size: var(--text-sm);
  color: var(--color-danger);
}
</style>
